export const LOG_LEVELS = ['DEBUG', 'INFO', 'WARN', 'ERROR'] as const;
export type LogLevel = (typeof LOG_LEVELS)[number];
export interface LogSink { write(level: LogLevel, message: string, detail?: unknown): void }

const rank = (level: LogLevel): number => LOG_LEVELS.indexOf(level);
const redact = (detail: unknown): unknown => {
  if (typeof detail !== 'object' || detail === null) return detail;
  return Object.fromEntries(Object.entries(detail).map(([key, value]) => [
    key, /credential|secret|token|password|api.?key/i.test(key) ? '[REDACTED]' : value,
  ]));
};

export class Logger {
  public constructor(private readonly sink: LogSink, private readonly minimumLevel: LogLevel = 'WARN') {}
  public debug(message: string, detail?: unknown): void { this.emit('DEBUG', message, detail); }
  public info(message: string, detail?: unknown): void { this.emit('INFO', message, detail); }
  public warn(message: string, detail?: unknown): void { this.emit('WARN', message, detail); }
  public error(message: string, detail?: unknown): void { this.emit('ERROR', message, detail); }
  private emit(level: LogLevel, message: string, detail?: unknown): void {
    if (rank(level) < rank(this.minimumLevel)) return;
    try { this.sink.write(level, message, redact(detail)); } catch { /* logger failures are isolated */ }
  }
}

export class ConsoleLogSink implements LogSink {
  public write(level: LogLevel, message: string, detail?: unknown): void {
    const method = level === 'ERROR' ? console.error : level === 'WARN' ? console.warn : console.info;
    if (detail === undefined) method(`[${level}] ${message}`); else method(`[${level}] ${message}`, detail);
  }
}
